import styled from "@emotion/styled";
import { AllCaps, Callout, FlexRow, H2, Link, Stack } from "atoms";
import { catalog, ExtraOCSubmissionsLink } from "features/commerce";
import { formatDate } from "utils/date";
import type { OpenCallTag } from "../types";

type OpenCallDetailsButtonProps = {
  // allowPaidSubmissions: boolean;
  isUser: boolean;
  // isEligible: boolean;
  tag: OpenCallTag;
  /** Array of Open Call IDs the current user has voted on */
  usersVotes?: string[];
  communityVotingStartDate?: string;
  numberSubmitted: number;
  openCallId: string;
  openCallTitle: string;
  /** Maximum number of submissions per person for this Open Call */
  maxSubmissions: number;
  /** User has not yet purchased paid Open Call Submissions */
  canPurchaseSubmissions: boolean;
  /** User's total number of submissions for this Open Call */
  userSubmissionAllowance: number;
  /** quantity of user's paid submissions for this open call */
  paidSubmissions: number;
  /** TODO: can we pass in one "track" function */
  trackBuySubmissions: VoidFunction;
  trackSignUpSubmit: VoidFunction;
  trackSignUpVote: VoidFunction;
  trackSignInSubmit: VoidFunction;
  trackSignInVote: VoidFunction;
  trackSubmit: VoidFunction;
  trackVote: VoidFunction;
};

// List of Open Call tags that do not have a rendered action
const nullStates: OpenCallTag[] = [
  "Coming Soon",
  "Artists Selected",
  "Concluded",
  "On Display",
  "Under Curation",
];

// Number of free Open Call Submissions
const FREE_SUBMISSIONS = 1;

function OpenCallDetailsButton({
  // allowPaidSubmissions,
  communityVotingStartDate,
  isUser,
  // isEligible,
  maxSubmissions,
  canPurchaseSubmissions,
  userSubmissionAllowance,
  numberSubmitted,
  openCallId,
  openCallTitle,
  usersVotes,
  tag,
  paidSubmissions,
  trackBuySubmissions,
  trackSignInSubmit,
  trackSignInVote,
  trackSignUpSubmit,
  trackSignUpVote,
  trackSubmit,
  trackVote,
}: OpenCallDetailsButtonProps) {
  if (nullStates.includes(tag)) {
    return null;
  }

  if (!isUser) {
    let action: React.ComponentProps<typeof SignInToAct>["action"] = "submit";
    if (tag === "Voting Soon") {
      action = "vote soon";
    } else if (tag === "Voting Open") {
      action = "vote";
    }

    return (
      <SignInToAct
        action={action}
        voteDate={communityVotingStartDate}
        trackSignUp={action === "vote" ? trackSignUpVote : trackSignUpSubmit}
        trackSignIn={action === "vote" ? trackSignInVote : trackSignInSubmit}
      />
    );
  }

  // Determine how many the user has remaining assuming they purchase all extras
  const maxRemaining = maxSubmissions - numberSubmitted;
  // Determine if the user can currently submit, i.e. check if they haven't yet met their allowance
  const canSubmit = numberSubmitted < userSubmissionAllowance;

  // TODO: still need this?
  const userMaxSubmissions = FREE_SUBMISSIONS + paidSubmissions;
  const remainingSubmissions = userMaxSubmissions - numberSubmitted;

  // Is user
  if (tag === "Submissions Open") {
    // TODO: Still need this?
    // Submitted at least 1.
    // if (!isEligible) return null;

    // If user has already submitted the max amount of submissions
    if (numberSubmitted >= maxSubmissions) {
      return null;
    }

    let submitText = `Additional Artwork${
      remainingSubmissions === 1 ? "" : "s"
    }`;
    if (numberSubmitted === 0) {
      submitText = canPurchaseSubmissions
        ? `${FREE_SUBMISSIONS} For Free`
        : "Now";
    }
    const submitVariant =
      numberSubmitted === 0 && canPurchaseSubmissions ? "secondary" : "primary";

    return (
      <FlexRow gap="10px">
        {canSubmit && (
          <Link
            arrow="r"
            onClick={trackSubmit}
            size="sm"
            to="submit"
            variant={submitVariant}
          >
            Submit {submitText}
          </Link>
        )}

        {canPurchaseSubmissions && (
          <ExtraOCSubmissionsLink
            arrow="r"
            onClick={trackBuySubmissions}
            openCallId={openCallId}
            title={openCallTitle}
            size="sm"
            variant="primary"
          >
            Submit{" "}
            <span>
              {numberSubmitted ? `${maxRemaining} More` : maxSubmissions}
            </span>{" "}
            For {catalog.extraOCSubmissions.cost}
          </ExtraOCSubmissionsLink>
        )}
      </FlexRow>
    );
  }

  if (tag === "Voting Open" && !usersVotes?.includes(openCallId)) {
    return (
      <Link to="vote" variant="primary" onClick={trackVote}>
        Vote now
      </Link>
    );
  }

  return null;
}

function SignInToAct({
  action,
  voteDate,
  trackSignUp,
  trackSignIn,
}: {
  action: "submit" | "vote" | "vote soon";
  trackSignUp: (a: string) => void;
  trackSignIn: (a: string) => void;
  voteDate: string | undefined;
}) {
  // Create Link `from` state to redirect to submission form after sign-in, not back here
  const submitLocation = {
    pathname: `${window.location.pathname}/${
      action === "vote soon" ? "" : action
    }`,
  };

  return (
    <SCallout>
      <FlexRow gap="10px">
        <Stack>
          <H2 size="sm">
            {action === "submit" && <>Sign in to submit your&nbsp;artwork</>}
            {action === "vote soon" && !voteDate && (
              <>
                Join <AllCaps>Hug</AllCaps> to vote
                {!voteDate
                  ? " soon!"
                  : ` on ${formatDate(voteDate, {
                      month: "long",
                      day: "numeric",
                    })}!`}
              </>
            )}
            {action === "vote" && <>Sign in to vote</>}
          </H2>
        </Stack>

        <FlexRow gap="10px">
          <Link
            to="/register"
            variant="primary"
            size="xs"
            state={{ from: submitLocation }}
            onClick={() => trackSignUp(action)}
          >
            Create Account
          </Link>
          {action !== "vote soon" && (
            <Link
              to="/sign-in"
              variant="primary"
              size="xs"
              state={{ from: submitLocation }}
              onClick={() => trackSignIn(action)}
            >
              Sign in
            </Link>
          )}
        </FlexRow>
      </FlexRow>
    </SCallout>
  );
}

const SCallout = styled(Callout)(({ theme }) => ({
  color: theme.colors.fg,
  flex: "0 0 100%",
  maxWidth: "max-content",
  "&&": { padding: ".66rem" },
}));

export { OpenCallDetailsButton };
